<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoicePayments',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoice: {
      type: Object,
      required: true,
    },
    reload: {
      type: null,
      required: false,
      default: null,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const triggerButtonID = ref(null);
  const ux = ref([]);
  const b_6466705138206980_style = computed(() => ({
    'min-width': '150px',
  }));

  const $data = computed(() => ({
    triggerButtonID: triggerButtonID.value,
    ux: ux.value,
    b_6466705138206980_style: b_6466705138206980_style.value,
  }));

  const $$load_triggerButtonID = async () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'triggerButtonID' },
      { name: 'ux' },
      { name: 'b_6466705138206980_style', nodeId: '6466705138206980' },
    ],
    loaders: { triggerButtonID: $$load_triggerButtonID },
    ux: {},
    dataRefs: { triggerButtonID, ux, b_6466705138206980_style },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const items = computed(() => {
    return props.invoice.payments.data;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.reload,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('reload');
    }
  );

  const act_4592790460410433_emit_3fh21garb_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_4592790460410433_close_2o65uhr8p_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_4592790460410433_showModal_spx7azwfo_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        oid: $event.scope.item.transaction.id,
      },
    };
  };
  const act_322074727583344_resource_f2yd09qny_getActionArgs = async (
    $event
  ) => {
    return {
      method: 'patch',
      target: props.invoice,
      data: {
        no_payment_required: $event.data.checked ? 'true' : null,
      },
    };
  };
  const act_5807883900791941_resource_lq0aa2oeu_getActionArgs = async (
    $event
  ) => {
    return {
      method: 'patch',
      target: props.invoice,
      data: {
        uncollectible: $event.data.checked ? 'true' : null,
      },
    };
  };
  const act_1984986731969919_emit_yoif1biie_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_1984986731969919_close_lyfug64ti_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_1984986731969919_showModal_5ral1sgog_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'cash_book',
        transactionType:
          props.invoice.invoice_kind == 'incoming' ? 'payment' : 'deposit',
        invoices: [props.invoice.id],
      },
    };
  };
  const act_298505142589676_emit_e5ych7m41_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_298505142589676_close_wr0656glx_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_298505142589676_showModal_v6n6rn1ve_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'bank',
        transactionType:
          props.invoice.invoice_kind == 'incoming' ? 'debit' : 'credit',
        invoices: [props.invoice.id],
      },
    };
  };
  const act_5738488038990219_emit_nnhez79ab_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_5738488038990219_close_39ee3v553_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_5738488038990219_showModal_1ylpe4oiq_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'technical',
        transactionType: 'invoice_reconciliation',
        invoices: [props.invoice.id],
      },
    };
  };
  const act_7481595298497917_emit_pvkaj0njn_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_7481595298497917_close_9kqab4xik_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_7481595298497917_showModal_tehoem1ry_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'technical',
        transactionType: 'write_off',
        invoices: [props.invoice.id],
      },
    };
  };

  const act_7315092382398562_reloadComponentData_qp8bvylwp =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_6uzagv23p =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_qp8bvylwp,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_6uzagv23p],
    event: 'reload',
  });
  const act_4592790460410433_emit_3fh21garb = new actionTypes.EmitAction({
    actionArgs: act_4592790460410433_emit_3fh21garb_getActionArgs,
  });
  const inst_evh_4592790460410433_close_2o65uhr8p =
    new actionTypes.EventHandlerImpl({
      action: act_4592790460410433_emit_3fh21garb,
      event: 'close',
      when: inst_evh_4592790460410433_close_2o65uhr8p_getWhen,
      displayName: 'emit',
    });
  const act_4592790460410433_showModal_spx7azwfo =
    new actionTypes.ShowModalAction({
      actionArgs: act_4592790460410433_showModal_spx7azwfo_getActionArgs,
      displayName: 'OpenPayment',
      events: [inst_evh_4592790460410433_close_2o65uhr8p],
    });
  const inst_evh_4592790460410433_click_imo1qpf44 =
    new actionTypes.EventHandlerImpl({
      action: act_4592790460410433_showModal_spx7azwfo,
      event: 'click',
      displayName: 'OpenPayment',
    });
  const inst_evh_4592790460410433_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4592790460410433_click_imo1qpf44],
    event: 'click',
  });
  const act_322074727583344_resource_f2yd09qny = new actionTypes.ResourceAction(
    {
      actionArgs: act_322074727583344_resource_f2yd09qny_getActionArgs,
    }
  );
  const inst_evh_322074727583344_change_pcbzbxxrf =
    new actionTypes.EventHandlerImpl({
      action: act_322074727583344_resource_f2yd09qny,
      event: 'change',
      displayName: 'resource',
    });
  const inst_evh_322074727583344_change = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_322074727583344_change_pcbzbxxrf],
    event: 'change',
  });
  const act_5807883900791941_resource_lq0aa2oeu =
    new actionTypes.ResourceAction({
      actionArgs: act_5807883900791941_resource_lq0aa2oeu_getActionArgs,
    });
  const inst_evh_5807883900791941_change_1heq1bf7s =
    new actionTypes.EventHandlerImpl({
      action: act_5807883900791941_resource_lq0aa2oeu,
      event: 'change',
      displayName: 'resource',
    });
  const inst_evh_5807883900791941_change = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5807883900791941_change_1heq1bf7s],
    event: 'change',
  });
  const act_1984986731969919_emit_yoif1biie = new actionTypes.EmitAction({
    actionArgs: act_1984986731969919_emit_yoif1biie_getActionArgs,
  });
  const inst_evh_1984986731969919_close_lyfug64ti =
    new actionTypes.EventHandlerImpl({
      action: act_1984986731969919_emit_yoif1biie,
      event: 'close',
      when: inst_evh_1984986731969919_close_lyfug64ti_getWhen,
      displayName: 'emit',
    });
  const act_1984986731969919_showModal_5ral1sgog =
    new actionTypes.ShowModalAction({
      actionArgs: act_1984986731969919_showModal_5ral1sgog_getActionArgs,
      displayName: 'createCashPayment',
      events: [inst_evh_1984986731969919_close_lyfug64ti],
    });
  const inst_evh_1984986731969919_clickPrevent_j1rb0ean0 =
    new actionTypes.EventHandlerImpl({
      action: act_1984986731969919_showModal_5ral1sgog,
      event: 'click.prevent',
      displayName: 'createCashPayment',
    });
  const inst_evh_1984986731969919_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1984986731969919_clickPrevent_j1rb0ean0],
      event: 'click.prevent',
    });
  const act_298505142589676_emit_e5ych7m41 = new actionTypes.EmitAction({
    actionArgs: act_298505142589676_emit_e5ych7m41_getActionArgs,
  });
  const inst_evh_298505142589676_close_wr0656glx =
    new actionTypes.EventHandlerImpl({
      action: act_298505142589676_emit_e5ych7m41,
      event: 'close',
      when: inst_evh_298505142589676_close_wr0656glx_getWhen,
      displayName: 'emit',
    });
  const act_298505142589676_showModal_v6n6rn1ve =
    new actionTypes.ShowModalAction({
      actionArgs: act_298505142589676_showModal_v6n6rn1ve_getActionArgs,
      displayName: 'createBankingPayment',
      events: [inst_evh_298505142589676_close_wr0656glx],
    });
  const inst_evh_298505142589676_clickPrevent_zsx3kdcez =
    new actionTypes.EventHandlerImpl({
      action: act_298505142589676_showModal_v6n6rn1ve,
      event: 'click.prevent',
      displayName: 'createBankingPayment',
    });
  const inst_evh_298505142589676_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_298505142589676_clickPrevent_zsx3kdcez],
      event: 'click.prevent',
    });
  const act_5738488038990219_emit_nnhez79ab = new actionTypes.EmitAction({
    actionArgs: act_5738488038990219_emit_nnhez79ab_getActionArgs,
  });
  const inst_evh_5738488038990219_close_39ee3v553 =
    new actionTypes.EventHandlerImpl({
      action: act_5738488038990219_emit_nnhez79ab,
      event: 'close',
      when: inst_evh_5738488038990219_close_39ee3v553_getWhen,
      displayName: 'emit',
    });
  const act_5738488038990219_showModal_1ylpe4oiq =
    new actionTypes.ShowModalAction({
      actionArgs: act_5738488038990219_showModal_1ylpe4oiq_getActionArgs,
      displayName: 'createReconciliation',
      events: [inst_evh_5738488038990219_close_39ee3v553],
    });
  const inst_evh_5738488038990219_clickPrevent_lou6la6eu =
    new actionTypes.EventHandlerImpl({
      action: act_5738488038990219_showModal_1ylpe4oiq,
      event: 'click.prevent',
      displayName: 'createReconciliation',
    });
  const inst_evh_5738488038990219_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5738488038990219_clickPrevent_lou6la6eu],
      event: 'click.prevent',
    });
  const act_7481595298497917_emit_pvkaj0njn = new actionTypes.EmitAction({
    actionArgs: act_7481595298497917_emit_pvkaj0njn_getActionArgs,
  });
  const inst_evh_7481595298497917_close_9kqab4xik =
    new actionTypes.EventHandlerImpl({
      action: act_7481595298497917_emit_pvkaj0njn,
      event: 'close',
      when: inst_evh_7481595298497917_close_9kqab4xik_getWhen,
      displayName: 'emit',
    });
  const act_7481595298497917_showModal_tehoem1ry =
    new actionTypes.ShowModalAction({
      actionArgs: act_7481595298497917_showModal_tehoem1ry_getActionArgs,
      displayName: 'createBadDebt',
      events: [inst_evh_7481595298497917_close_9kqab4xik],
    });
  const inst_evh_7481595298497917_clickPrevent_m0v8l2ank =
    new actionTypes.EventHandlerImpl({
      action: act_7481595298497917_showModal_tehoem1ry,
      event: 'click.prevent',
      displayName: 'createBadDebt',
    });
  const inst_evh_7481595298497917_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7481595298497917_clickPrevent_m0v8l2ank],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_7315092382398562_reload_6uzagv23p,
    inst_evh_7315092382398562_reload,
    inst_evh_4592790460410433_close_2o65uhr8p,
    inst_evh_4592790460410433_click_imo1qpf44,
    OpenPayment: inst_evh_4592790460410433_click_imo1qpf44,
    inst_evh_4592790460410433_click,
    inst_evh_322074727583344_change_pcbzbxxrf,
    inst_evh_322074727583344_change,
    inst_evh_5807883900791941_change_1heq1bf7s,
    inst_evh_5807883900791941_change,
    inst_evh_1984986731969919_close_lyfug64ti,
    inst_evh_1984986731969919_clickPrevent_j1rb0ean0,
    createCashPayment: inst_evh_1984986731969919_clickPrevent_j1rb0ean0,
    inst_evh_1984986731969919_clickPrevent,
    inst_evh_298505142589676_close_wr0656glx,
    inst_evh_298505142589676_clickPrevent_zsx3kdcez,
    createBankingPayment: inst_evh_298505142589676_clickPrevent_zsx3kdcez,
    inst_evh_298505142589676_clickPrevent,
    inst_evh_5738488038990219_close_39ee3v553,
    inst_evh_5738488038990219_clickPrevent_lou6la6eu,
    createReconciliation: inst_evh_5738488038990219_clickPrevent_lou6la6eu,
    inst_evh_5738488038990219_clickPrevent,
    inst_evh_7481595298497917_close_9kqab4xik,
    inst_evh_7481595298497917_clickPrevent_m0v8l2ank,
    createBadDebt: inst_evh_7481595298497917_clickPrevent_m0v8l2ank,
    inst_evh_7481595298497917_clickPrevent,
  };

  const evh_7315092382398562_reload_6uzagv23p = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_6uzagv23p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4592790460410433_close_2o65uhr8p = async (event, scope) => {
    return await actions.inst_evh_4592790460410433_close_2o65uhr8p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4592790460410433_click_imo1qpf44 = async (event, scope) => {
    return await actions.inst_evh_4592790460410433_click_imo1qpf44.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4592790460410433_click = async (event, scope) => {
    return await actions.inst_evh_4592790460410433_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_322074727583344_change_pcbzbxxrf = async (event, scope) => {
    return await actions.inst_evh_322074727583344_change_pcbzbxxrf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_322074727583344_change = async (event, scope) => {
    return await actions.inst_evh_322074727583344_change.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5807883900791941_change_1heq1bf7s = async (event, scope) => {
    return await actions.inst_evh_5807883900791941_change_1heq1bf7s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5807883900791941_change = async (event, scope) => {
    return await actions.inst_evh_5807883900791941_change.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1984986731969919_close_lyfug64ti = async (event, scope) => {
    return await actions.inst_evh_1984986731969919_close_lyfug64ti.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1984986731969919_clickPrevent_j1rb0ean0 = async (event, scope) => {
    return await actions.inst_evh_1984986731969919_clickPrevent_j1rb0ean0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1984986731969919_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1984986731969919_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_298505142589676_close_wr0656glx = async (event, scope) => {
    return await actions.inst_evh_298505142589676_close_wr0656glx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_298505142589676_clickPrevent_zsx3kdcez = async (event, scope) => {
    return await actions.inst_evh_298505142589676_clickPrevent_zsx3kdcez.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_298505142589676_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_298505142589676_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5738488038990219_close_39ee3v553 = async (event, scope) => {
    return await actions.inst_evh_5738488038990219_close_39ee3v553.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5738488038990219_clickPrevent_lou6la6eu = async (event, scope) => {
    return await actions.inst_evh_5738488038990219_clickPrevent_lou6la6eu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5738488038990219_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_5738488038990219_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7481595298497917_close_9kqab4xik = async (event, scope) => {
    return await actions.inst_evh_7481595298497917_close_9kqab4xik.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7481595298497917_clickPrevent_m0v8l2ank = async (event, scope) => {
    return await actions.inst_evh_7481595298497917_clickPrevent_m0v8l2ank.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7481595298497917_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_7481595298497917_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox direction="column">
      <ItpBox direction="column" v-if="items && items.length">
        <ItpBox
          :key="item.id"
          alignItems="start"
          class="border rounded rounded-3 p-2 mb-2 w-100"
          v-for="item in items"
          :style="b_6466705138206980_style"
        >
          <ItpText
            strong
            :text="date(item.transaction.transaction_date)"
            class="me-3"
          >
          </ItpText>
          <ItpBox direction="column" class="me-3">
            <div>
              <ItpLink
                :text="item.transaction.transaction_number"
                strong
                @click="evh_4592790460410433_click($event, { item })"
              >
              </ItpLink>
            </div>
            <UcAccountMiniCard :item="item.account"> </UcAccountMiniCard>
          </ItpBox>
          <ItpBox class="ms-auto">
            <UcPaymentTransactionTypeSymbol
              :transactionType="item.transaction.transaction_type"
              class="mx-2"
            >
            </UcPaymentTransactionTypeSymbol>
            <ItpText strong class="ms-auto">
              {{ number(item.amount) }} {{ item.currency }}
            </ItpText>
          </ItpBox>
        </ItpBox>
      </ItpBox>
      <ItpBox class="px-2 mb-2 w-100" v-if="items && items.length > 1">
        <ItpText>
          {{ pgettext('InvoicePayments', 'Total') }}
        </ItpText>
        <ItpText strong class="ms-auto">
          {{ number(invoice.totals.paid) }} {{ invoice.currency }}
        </ItpText>
      </ItpBox>
      <ItpBox direction="row" alignItems="center">
        <div class="form-check px-0">
          <ItpFormCheckbox
            name="no_payment_required"
            v-model="invoice.no_payment_required"
            :value="invoice.no_payment_required"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (invoice.no_payment_required =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            is-switch
            class="py-1"
            @change="evh_322074727583344_change($event, {})"
          >
            {{ pgettext('Invoice', 'No payment required') }}
          </ItpFormCheckbox>
          <ItpFormCheckbox
            name="uncollectible"
            v-model="invoice.uncollectible"
            :value="invoice.uncollectible"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (invoice.uncollectible =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            is-switch
            class="py-1"
            @change="evh_5807883900791941_change($event, {})"
          >
            {{ pgettext('Invoice', 'Uncollectible') }}
          </ItpFormCheckbox>
        </div>
        <BDropdown right variant="light" class="border, ms-auto">
          <template #button-content>
            <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
            <ItpText :text="pgettext('InvoicePayments', 'New payment')">
            </ItpText>
          </template>
          <BDropdownItemButton
            @click.prevent="evh_1984986731969919_clickPrevent($event, {})"
          >
            <ItpText :text="pgettext('InvoicePayments', 'New cash payment...')">
            </ItpText>
          </BDropdownItemButton>
          <BDropdownItemButton
            @click.prevent="evh_298505142589676_clickPrevent($event, {})"
          >
            <ItpText
              :text="pgettext('InvoicePayments', 'New banking payment...')"
            >
            </ItpText>
          </BDropdownItemButton>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <BDropdownItemButton
            @click.prevent="evh_5738488038990219_clickPrevent($event, {})"
          >
            <ItpText
              :text="pgettext('InvoicePayments', 'New reconciliation...')"
            >
            </ItpText>
          </BDropdownItemButton>
          <BDropdownItemButton
            @click.prevent="evh_7481595298497917_clickPrevent($event, {})"
          >
            <ItpText :text="pgettext('InvoicePayments', 'Bad debt...')">
            </ItpText>
          </BDropdownItemButton>
        </BDropdown>
      </ItpBox>
    </ItpBox>
  </ItpUsercontrol>
</template>
